import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtUGX, shortDate } from "@/lib/format";
import { ShoppingCart, CheckCircle2, XCircle, PackageCheck, FileCheck, Plus, Sparkles, Trash2, ClipboardList, Layers } from "lucide-react";
import { toast } from "sonner";
import {
  listProcurementDashboard, createProcurementRequest, decideProcurementRequest,
  issuePurchaseOrder, recordGoodsReceipt, recordSupplierInvoice, runThreeWayMatch,
  createVendor, seedProcurementDemo,
} from "@/lib/procurement.functions";

type Line = { item_name: string; quantity: number; unit_of_measure: string; estimated_unit_price: number };

export function ProcurementWorkspace({ schoolId }: { schoolId: string }) {
  const qc = useQueryClient();
  const load = useServerFn(listProcurementDashboard);
  const { data, isLoading } = useQuery({
    queryKey: ["procurement", schoolId],
    queryFn: () => load({ data: { schoolId } }),
  });

  const seed = useMutation({
    mutationFn: useServerFn(seedProcurementDemo),
    onSuccess: (r: any) => {
      toast.success(r?.seeded ? "Procurement demo seeded" : "Already has data");
      qc.invalidateQueries({ queryKey: ["procurement", schoolId] });
    },
  });

  const [newOpen, setNewOpen] = useState(false);
  const [poOpen, setPoOpen] = useState<{ requestId: string; total: number } | null>(null);
  const [grnOpen, setGrnOpen] = useState<any | null>(null);
  const [invOpen, setInvOpen] = useState<any | null>(null);
  const [vendorOpen, setVendorOpen] = useState(false);

  const requests = data?.requests ?? [];
  const pos = data?.pos ?? [];
  const vendors = data?.vendors ?? [];
  const invoices = data?.invoices ?? [];
  const budgetLines = data?.budgetLines ?? [];
  const audit = data?.audit ?? [];

  const empty = !isLoading && requests.length === 0 && pos.length === 0 && vendors.length === 0;

  return (
    <div className="space-y-4">
      <Card className="relative overflow-hidden bg-gradient-to-br from-amber-900 via-amber-800 to-amber-950 p-6 text-amber-50">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-200/70">Procurement command</div>
            <div className="mt-1 font-display text-2xl">Requests → RFQ → PO → Goods → Match → Pay</div>
            <div className="mt-1 text-sm text-amber-100/80">Every purchase carries a 10-year audit trail and a three-way match before payment.</div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setVendorOpen(true)} className="bg-amber-50 text-amber-900 hover:bg-amber-100"><Plus className="mr-1 h-4 w-4" />Vendor</Button>
            <Button variant="secondary" onClick={() => setNewOpen(true)} className="bg-amber-50 text-amber-900 hover:bg-amber-100"><Plus className="mr-1 h-4 w-4" />New request</Button>
          </div>
        </div>
        <div className="pointer-events-none absolute inset-0 opacity-30 [background:radial-gradient(circle_at_20%_10%,white,transparent_45%)]" />
      </Card>

      {empty && (
        <Card className="border-dashed p-8 text-center">
          <ShoppingCart className="mx-auto h-10 w-10 text-muted-foreground" />
          <div className="mt-3 font-display text-xl">No procurement records yet</div>
          <div className="mt-1 text-sm text-muted-foreground">Seed a realistic demo (2 vendors, budget, request, PO, commitment) to explore the flow.</div>
          <Button className="mt-4" onClick={() => seed.mutate({ data: { schoolId } })} disabled={seed.isPending}>
            <Sparkles className="mr-1 h-4 w-4" />{seed.isPending ? "Seeding…" : "Seed demo procurement"}
          </Button>
        </Card>
      )}

      <Tabs defaultValue="requests">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="requests">Requests</TabsTrigger>
          <TabsTrigger value="pos">Purchase orders</TabsTrigger>
          <TabsTrigger value="invoices">Invoices & match</TabsTrigger>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
          <TabsTrigger value="budgets">Budgets</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-3 space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            {(["submitted","approved","po_issued","rejected"]).map(status => (
              <Card key={status} className="p-3">
                <div className="mb-2 flex items-center justify-between">
                  <Badge variant="outline" className="capitalize">{status.replace('_',' ')}</Badge>
                  <span className="text-xs text-muted-foreground">{requests.filter((r: any) => r.status === status).length}</span>
                </div>
                <div className="space-y-2">
                  {requests.filter((r: any) => r.status === status).slice(0, 8).map((r: any) => (
                    <div key={r.id} className="rounded-md border bg-card p-2 text-sm">
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{r.request_no}</div>
                        <div className="text-xs text-muted-foreground">{fmtUGX(r.estimated_total_ugx)}</div>
                      </div>
                      <div className="text-xs text-muted-foreground">{r.department} · needs {r.needed_by ? shortDate(r.needed_by) : "—"}</div>
                      <div className="mt-2 flex gap-2">
                        {r.status === "submitted" && (
                          <>
                            <ApproveBtn id={r.id} schoolId={schoolId} />
                            <RejectBtn id={r.id} schoolId={schoolId} />
                          </>
                        )}
                        {r.status === "approved" && (
                          <Button size="sm" variant="outline" onClick={() => setPoOpen({ requestId: r.id, total: r.estimated_total_ugx })}>Issue PO</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="pos" className="mt-3">
          <Card className="p-3">
            <div className="space-y-2">
              {pos.map((po: any) => (
                <div key={po.id} className="grid grid-cols-6 gap-2 rounded-md border p-2 text-sm">
                  <div className="font-medium">{po.po_no}</div>
                  <div className="text-muted-foreground">{po.vendors?.vendor_name}</div>
                  <div>{fmtUGX(po.total_amount)}</div>
                  <div><Badge variant="outline" className="capitalize">{po.status.replace('_',' ')}</Badge></div>
                  <div className="text-xs text-muted-foreground">exp {po.expected_delivery_date ? shortDate(po.expected_delivery_date) : "—"}</div>
                  <div className="flex justify-end gap-1">
                    {po.status === "issued" && <Button size="sm" variant="outline" onClick={() => setGrnOpen(po)}><PackageCheck className="mr-1 h-3 w-3" />GRN</Button>}
                    {po.status !== "invoiced" && <Button size="sm" variant="ghost" onClick={() => setInvOpen(po)}><FileCheck className="mr-1 h-3 w-3" />Invoice</Button>}
                  </div>
                </div>
              ))}
              {pos.length === 0 && <div className="p-4 text-center text-sm text-muted-foreground">No purchase orders</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-3">
          <Card className="p-3 space-y-2">
            {invoices.map((inv: any) => (
              <div key={inv.id} className="grid grid-cols-6 gap-2 rounded-md border p-2 text-sm items-center">
                <div className="font-medium">{inv.invoice_no}</div>
                <div className="text-muted-foreground">{inv.vendors?.vendor_name}</div>
                <div>{fmtUGX(inv.amount)}</div>
                <div className="text-xs">{shortDate(inv.invoice_date)}</div>
                <div><Badge variant={inv.status === "matched" ? "default" : "outline"} className="capitalize">{inv.status}</Badge></div>
                <div className="flex justify-end">
                  <MatchBtn id={inv.id} schoolId={schoolId} />
                </div>
              </div>
            ))}
            {invoices.length === 0 && <div className="p-4 text-center text-sm text-muted-foreground">No supplier invoices yet</div>}
          </Card>
        </TabsContent>

        <TabsContent value="vendors" className="mt-3">
          <Card className="p-3">
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {vendors.map((v: any) => (
                <Card key={v.id} className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{v.vendor_name}</div>
                      <div className="text-xs text-muted-foreground">{v.category ?? "goods"} · TIN {v.tax_id ?? "—"}</div>
                    </div>
                    <Badge variant={v.status === "active" ? "default" : "outline"}>{v.status}</Badge>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <Stat label="Rating" value={`${Number(v.rating_score).toFixed(1)}★`} />
                    <Stat label="On-time" value={`${Number(v.on_time_pct).toFixed(0)}%`} />
                    <Stat label="Defect" value={`${Number(v.defect_pct).toFixed(1)}%`} />
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">{v.phone} · {v.email}</div>
                </Card>
              ))}
              {vendors.length === 0 && <div className="col-span-full p-6 text-center text-sm text-muted-foreground">No vendors yet</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="budgets" className="mt-3">
          <Card className="p-3 space-y-2">
            {budgetLines.map((b: any) => {
              const total = b.allocated_amount || 1;
              const usedPct = ((b.committed_amount + b.spent_amount) / total) * 100;
              return (
                <div key={b.id} className="rounded-md border p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{b.department} · <span className="text-muted-foreground">{b.budget_code}</span></div>
                      <div className="text-xs text-muted-foreground">Allocated {fmtUGX(b.allocated_amount)} · Committed {fmtUGX(b.committed_amount)} · Spent {fmtUGX(b.spent_amount)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-lg">{fmtUGX(b.available_amount)}</div>
                      <div className="text-xs text-muted-foreground">available</div>
                    </div>
                  </div>
                  <Progress value={Math.min(100, usedPct)} className="mt-2 h-2" />
                </div>
              );
            })}
            {budgetLines.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No budgets</div>}
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="mt-3">
          <Card className="p-3 space-y-1">
            {audit.map((a: any) => (
              <div key={a.id} className="flex items-center justify-between rounded-md border p-2 text-sm">
                <div>
                  <div className="font-medium capitalize">{a.action} · {a.entity_type}</div>
                  <div className="text-xs text-muted-foreground">{shortDate(a.created_at)}</div>
                </div>
                <Badge variant="outline"><ClipboardList className="mr-1 h-3 w-3" />retained 10y</Badge>
              </div>
            ))}
            {audit.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No audit records yet</div>}
          </Card>
        </TabsContent>
      </Tabs>

      <NewRequestDialog open={newOpen} onClose={() => setNewOpen(false)} schoolId={schoolId} />
      {poOpen && <IssuePODialog vendors={vendors} requestId={poOpen.requestId} schoolId={schoolId} onClose={() => setPoOpen(null)} />}
      {grnOpen && <GRNDialog po={grnOpen} onClose={() => setGrnOpen(null)} schoolId={schoolId} />}
      {invOpen && <InvoiceDialog po={invOpen} onClose={() => setInvOpen(null)} schoolId={schoolId} />}
      <VendorDialog open={vendorOpen} onClose={() => setVendorOpen(false)} schoolId={schoolId} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="rounded-md bg-muted/50 p-2"><div className="text-sm font-semibold">{value}</div><div className="text-[10px] uppercase text-muted-foreground">{label}</div></div>;
}

function ApproveBtn({ id, schoolId }: { id: string; schoolId: string }) {
  const qc = useQueryClient();
  const decide = useMutation({
    mutationFn: useServerFn(decideProcurementRequest),
    onSuccess: () => { toast.success("Approved"); qc.invalidateQueries({ queryKey: ["procurement", schoolId] }); },
  });
  return <Button size="sm" onClick={() => decide.mutate({ data: { requestId: id, decision: "approve" } })}><CheckCircle2 className="mr-1 h-3 w-3" />Approve</Button>;
}
function RejectBtn({ id, schoolId }: { id: string; schoolId: string }) {
  const qc = useQueryClient();
  const decide = useMutation({
    mutationFn: useServerFn(decideProcurementRequest),
    onSuccess: () => { toast.success("Rejected"); qc.invalidateQueries({ queryKey: ["procurement", schoolId] }); },
  });
  return <Button size="sm" variant="outline" onClick={() => decide.mutate({ data: { requestId: id, decision: "reject", reason: "Budget exhausted" } })}><XCircle className="mr-1 h-3 w-3" />Reject</Button>;
}
function MatchBtn({ id, schoolId }: { id: string; schoolId: string }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: useServerFn(runThreeWayMatch),
    onSuccess: (r: any) => { toast.success(`Match: ${r.status} · variance ${fmtUGX(r.variance_amount || 0)}`); qc.invalidateQueries({ queryKey: ["procurement", schoolId] }); },
    onError: (e: any) => toast.error(e.message),
  });
  return <Button size="sm" variant="outline" onClick={() => m.mutate({ data: { invoiceId: id } })}>3-way match</Button>;
}

function NewRequestDialog({ open, onClose, schoolId }: { open: boolean; onClose: () => void; schoolId: string }) {
  const qc = useQueryClient();
  const [dept, setDept] = useState("Boarding");
  const [budget, setBudget] = useState("BORD-T2-FOOD");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<Line[]>([{ item_name: "", quantity: 1, unit_of_measure: "each", estimated_unit_price: 0 }]);
  const create = useMutation({
    mutationFn: useServerFn(createProcurementRequest),
    onSuccess: () => { toast.success("Request submitted"); qc.invalidateQueries({ queryKey: ["procurement", schoolId] }); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });
  const total = lines.reduce((s, l) => s + l.quantity * l.estimated_unit_price, 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New procurement request</DialogTitle><DialogDescription>Runs a budget check on submit.</DialogDescription></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Department</Label><Input value={dept} onChange={e => setDept(e.target.value)} /></div>
            <div><Label>Budget code</Label><Input value={budget} onChange={e => setBudget(e.target.value)} /></div>
          </div>
          <div><Label>Notes</Label><Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} /></div>
          <div>
            <div className="mb-1 flex items-center justify-between"><Label>Line items</Label><Button size="sm" variant="ghost" onClick={() => setLines([...lines, { item_name: "", quantity: 1, unit_of_measure: "each", estimated_unit_price: 0 }])}><Plus className="h-3 w-3" /></Button></div>
            {lines.map((l, i) => (
              <div key={i} className="mt-1 grid grid-cols-12 gap-1">
                <Input className="col-span-5" placeholder="Item" value={l.item_name} onChange={e => { const c = [...lines]; c[i].item_name = e.target.value; setLines(c); }} />
                <Input className="col-span-2" type="number" value={l.quantity} onChange={e => { const c = [...lines]; c[i].quantity = Number(e.target.value); setLines(c); }} />
                <Input className="col-span-2" placeholder="Unit" value={l.unit_of_measure} onChange={e => { const c = [...lines]; c[i].unit_of_measure = e.target.value; setLines(c); }} />
                <Input className="col-span-2" type="number" placeholder="Unit ₴" value={l.estimated_unit_price} onChange={e => { const c = [...lines]; c[i].estimated_unit_price = Number(e.target.value); setLines(c); }} />
                <Button size="icon" variant="ghost" className="col-span-1" onClick={() => setLines(lines.filter((_, x) => x !== i))}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
            <div className="mt-2 text-right text-sm font-medium">Est. total {fmtUGX(total)}</div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button disabled={create.isPending || !lines.some(l => l.item_name)} onClick={() => create.mutate({ data: { schoolId, department: dept, budgetCode: budget, notes, lines: lines.filter(l => l.item_name) } })}>Submit</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IssuePODialog({ requestId, schoolId, vendors, onClose }: { requestId: string; schoolId: string; vendors: any[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [vendor, setVendor] = useState<string>(vendors[0]?.id ?? "");
  const [eta, setEta] = useState(new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10));
  const [lines, setLines] = useState<{ item_name: string; quantity_ordered: number; unit_price: number }[]>([
    { item_name: "Rice 50kg bag", quantity_ordered: 20, unit_price: 180000 },
    { item_name: "Cooking oil 20L", quantity_ordered: 10, unit_price: 90000 },
  ]);
  const issue = useMutation({
    mutationFn: useServerFn(issuePurchaseOrder),
    onSuccess: (r: any) => { toast.success(`PO ${r.po_no} issued`); qc.invalidateQueries({ queryKey: ["procurement", schoolId] }); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Issue purchase order</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Vendor</Label>
              <Select value={vendor} onValueChange={setVendor}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Expected delivery</Label><Input type="date" value={eta} onChange={e => setEta(e.target.value)} /></div>
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-1">
              <Input className="col-span-6" value={l.item_name} onChange={e => { const c = [...lines]; c[i].item_name = e.target.value; setLines(c); }} />
              <Input className="col-span-2" type="number" value={l.quantity_ordered} onChange={e => { const c = [...lines]; c[i].quantity_ordered = Number(e.target.value); setLines(c); }} />
              <Input className="col-span-4" type="number" value={l.unit_price} onChange={e => { const c = [...lines]; c[i].unit_price = Number(e.target.value); setLines(c); }} />
            </div>
          ))}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button disabled={!vendor || issue.isPending} onClick={() => issue.mutate({ data: { schoolId, requestId, vendorId: vendor, expectedDelivery: eta, lines } })}>Issue PO</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GRNDialog({ po, onClose, schoolId }: { po: any; onClose: () => void; schoolId: string }) {
  const qc = useQueryClient();
  const [lines, setLines] = useState(
    (po.purchase_order_lines ?? []).map((l: any) => ({
      purchase_order_line_id: l.id, quantity_received: Number(l.quantity_ordered),
      quantity_accepted: Number(l.quantity_ordered), quantity_rejected: 0, condition_notes: "",
    }))
  );
  const [remarks, setRemarks] = useState("");
  const rec = useMutation({
    mutationFn: useServerFn(recordGoodsReceipt),
    onSuccess: (r: any) => { toast.success(`GRN ${r.grn_no} captured`); qc.invalidateQueries({ queryKey: ["procurement", schoolId] }); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Goods receipt · {po.po_no}</DialogTitle></DialogHeader>
        <div className="grid gap-2">
          {(po.purchase_order_lines ?? []).map((l: any, i: number) => (
            <div key={l.id} className="grid grid-cols-12 items-center gap-1">
              <div className="col-span-5 text-sm">{l.item_name} <span className="text-muted-foreground">({l.quantity_ordered})</span></div>
              <Input className="col-span-2" type="number" value={lines[i].quantity_received} onChange={e => { const c = [...lines]; c[i].quantity_received = Number(e.target.value); setLines(c); }} placeholder="rcv" />
              <Input className="col-span-2" type="number" value={lines[i].quantity_accepted} onChange={e => { const c = [...lines]; c[i].quantity_accepted = Number(e.target.value); setLines(c); }} placeholder="acc" />
              <Input className="col-span-3" type="number" value={lines[i].quantity_rejected} onChange={e => { const c = [...lines]; c[i].quantity_rejected = Number(e.target.value); setLines(c); }} placeholder="rej" />
            </div>
          ))}
          <Textarea rows={2} placeholder="Remarks" value={remarks} onChange={e => setRemarks(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => rec.mutate({ data: { purchaseOrderId: po.id, remarks, lines } })} disabled={rec.isPending}>Confirm receipt</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDialog({ po, onClose, schoolId }: { po: any; onClose: () => void; schoolId: string }) {
  const qc = useQueryClient();
  const [invNo, setInvNo] = useState("INV-" + Math.floor(Math.random() * 90000 + 10000));
  const [amount, setAmount] = useState<number>(po.total_amount);
  const rec = useMutation({
    mutationFn: useServerFn(recordSupplierInvoice),
    onSuccess: () => { toast.success("Invoice recorded"); qc.invalidateQueries({ queryKey: ["procurement", schoolId] }); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Supplier invoice · {po.po_no}</DialogTitle></DialogHeader>
        <div className="grid gap-2">
          <div><Label>Invoice no.</Label><Input value={invNo} onChange={e => setInvNo(e.target.value)} /></div>
          <div><Label>Amount (UGX)</Label><Input type="number" value={amount} onChange={e => setAmount(Number(e.target.value))} /></div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => rec.mutate({ data: { vendorId: po.vendor_id, purchaseOrderId: po.id, invoiceNo: invNo, amount } })} disabled={rec.isPending}>Record & match later</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VendorDialog({ open, onClose, schoolId }: { open: boolean; onClose: () => void; schoolId: string }) {
  const qc = useQueryClient();
  const [name, setName] = useState(""); const [cat, setCat] = useState("goods");
  const [phone, setPhone] = useState(""); const [email, setEmail] = useState(""); const [tin, setTin] = useState("");
  const create = useMutation({
    mutationFn: useServerFn(createVendor),
    onSuccess: () => { toast.success("Vendor onboarded"); qc.invalidateQueries({ queryKey: ["procurement", schoolId] }); onClose(); setName(""); setPhone(""); setEmail(""); setTin(""); },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Onboard a vendor</DialogTitle></DialogHeader>
        <div className="grid gap-2">
          <div><Label>Business name</Label><Input value={name} onChange={e => setName(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Category</Label>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["goods","services","canteen","stationery","ict","transport","facilities"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>URA TIN</Label><Input value={tin} onChange={e => setTin(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Phone</Label><Input value={phone} onChange={e => setPhone(e.target.value)} /></div>
            <div><Label>Email</Label><Input value={email} onChange={e => setEmail(e.target.value)} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button disabled={!name || create.isPending} onClick={() => create.mutate({ data: { schoolId, vendor_name: name, category: cat, phone, email, tax_id: tin } })}><Layers className="mr-1 h-4 w-4" />Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
